import React, { useCallback, useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { selectBestPosition } from '../utils/tooltipPositioning';
import { formatPLN } from '@repo/domain';

/**
 * PropertySVGOverlayV2 - Navigator V2 format renderer
 * Renders shapes from navigator_config_v2 format (points array instead of svgPath string)
 * Supports navigation between artboards via navigateTo attribute
 * Colors shapes based on property status from database
 */

// Format area with m² unit
const formatArea = (value) => {
  if (!value && value !== 0) return null;
  return `${value.toFixed(2).replace('.', ',')} m²`;
};

// Format price per m²
const formatPricePerM2 = (price, area) => {
  if (!price || !area || area === 0) return null;
  const pricePerM2 = price / area;
  return formatPLN(pricePerM2);
};

// Status color mapping
const STATUS_COLORS = {
  available: '#4CAF50',
  reserved: '#FF9800',
  sold: '#F44336',
  coming_soon: '#BDBDBD'
};

// Default style for shapes without matching property (navigation shapes etc.)
// No fill by default, white only on hover
const DEFAULT_FILL = 'transparent';
const DEFAULT_FILL_OPACITY = 0;
const DEFAULT_HOVER_FILL = '#ffffff';
const DEFAULT_HOVER_FILL_OPACITY = 0.3;

// Convert points array to SVG path string
const pointsToPath = (points) => {
  if (!points || points.length < 3) return '';

  return points.map((point, index) => {
    const command = index === 0 ? 'M' : 'L';
    return `${command} ${point.x} ${point.y}`;
  }).join(' ') + ' Z';
};

// Get the identifier used for selection (externalId matches property.id in the app)
const getShapeIdentifier = (shape) => shape.data?.externalId || shape.id;

// Normalize ID for matching (handles format differences like "MP01" vs "MP 03")
const normalizeId = (id) => {
  if (!id) return '';
  // Remove spaces and leading zeros from numbers
  return id.replace(/\s+/g, '').replace(/(\D)0+(\d)/g, '$1$2').toUpperCase();
};

// Get navigation target if shape has navigateTo
const getNavigationTarget = (shape) => shape.data?.navigateTo || null;

// Get color based on property status
const getStatusColor = (property) => {
  if (!property) return null;
  // status is an object with code property (from resolveStatus)
  const statusCode = property.status?.code || property.statusCode;
  return STATUS_COLORS[statusCode] || null;
};

// Individual shape region component
function ShapeRegion({
  shape,
  property,
  isSelected,
  isHovered,
  onClick,
  onHover,
  onNavigate,
  onTooltip,
  isTouchDevice,
  currentArtboard,
  defaultSoldForUnmatched
}) {
  const shapeId = getShapeIdentifier(shape);
  const navigateTo = getNavigationTarget(shape);

  const handleClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (shape.clickable !== false) {
      // If shape has navigateTo, handle navigation
      if (navigateTo && onNavigate) {
        onNavigate(navigateTo);
      } else {
        // Otherwise handle as property selection
        onClick(shapeId, event);
      }
    }
  }, [shape.clickable, shapeId, navigateTo, onClick, onNavigate]);

  const handleMouseEnter = useCallback((event) => {
    onHover(shapeId);
    if (onTooltip) {
      onTooltip(shapeId, event.nativeEvent);
    }
  }, [shapeId, onHover, onTooltip]);

  const handleMouseMove = useCallback((event) => {
    if (onTooltip) {
      onTooltip(shapeId, event.nativeEvent);
    }
  }, [shapeId, onTooltip]);

  const handleMouseLeave = useCallback(() => {
    onHover(null);
    if (onTooltip) {
      onTooltip(null, null);
    }
  }, [onHover, onTooltip]);

  const handleTouchStart = useCallback((event) => {
    event.preventDefault();
    onHover(shapeId);
    // Show tooltip on touch using touch coordinates
    if (onTooltip && event.touches && event.touches[0]) {
      const touch = event.touches[0];
      onTooltip(shapeId, { clientX: touch.clientX, clientY: touch.clientY });
    }
  }, [shapeId, onHover, onTooltip]);

  const handleTouchEnd = useCallback((event) => {
    event.preventDefault();
    if (shape.clickable !== false) {
      // If shape has navigateTo, handle navigation
      if (navigateTo && onNavigate) {
        onNavigate(navigateTo);
      } else {
        onClick(shapeId, event);
      }
    }
    // On mobile, don't auto-hide tooltip - user will close it manually
    // Only clear hover state
    onHover(null);
  }, [shape.clickable, shapeId, navigateTo, onClick, onNavigate, onHover]);

  if (!shape.points || shape.points.length < 3) {
    return null;
  }

  const svgPath = pointsToPath(shape.points);

  // Determine fill color and opacity based on property status and hover state
  const statusColor = getStatusColor(property);

  // Check if property is available (not sold or reserved)
  const isAvailable = property?.status?.code === 'available';
  const isUnavailable = property && !isAvailable;

  let fillColor;
  let fillOpacity;

  if (property) {
    // Property exists - use status color with appropriate opacity
    fillColor = statusColor;
    // For unavailable properties, don't change opacity on hover
    if (isUnavailable) {
      fillOpacity = 0.4;
    } else {
      fillOpacity = (isSelected || isHovered) ? 0.6 : 0.4;
    }
  } else {
    // No property - check global defaultSoldForUnmatched flag
    // Skip for:
    // 1. Navigation shapes (have navigateTo attribute)
    // 2. Navigation artboards (id contains "selector")
    const isNavigationShape = !!navigateTo;
    const isNavigationArtboard = currentArtboard?.id?.includes('selector');
    const shouldDefaultToSold = defaultSoldForUnmatched && !isNavigationShape && !isNavigationArtboard;

    if (shouldDefaultToSold) {
      // Treat as sold - red color, fixed opacity
      fillColor = STATUS_COLORS.sold;
      fillOpacity = 0.4;
    } else if (isSelected || isHovered) {
      fillColor = DEFAULT_HOVER_FILL;
      fillOpacity = DEFAULT_HOVER_FILL_OPACITY;
    } else {
      fillColor = DEFAULT_FILL;
      fillOpacity = DEFAULT_FILL_OPACITY;
    }
  }

  // Determine cursor and clickability
  const isClickable = shape.clickable !== false && (!property || isAvailable);

  return (
    <g className="property-region-group">
      <path
        d={svgPath}
        className={`property-region ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${isUnavailable ? 'unavailable' : ''}`}
        fill={fillColor}
        fillOpacity={fillOpacity}
        stroke="rgba(255, 255, 255, 0.3)"
        strokeWidth={1}
        style={{
          cursor: isClickable ? 'pointer' : 'not-allowed',
          pointerEvents: 'auto'
        }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      />
    </g>
  );
}

// Tooltip component for non-list items (parking, storage)
function MapTooltip({ property, position, containerRef, isMobile, onClose }) {
  // Refs and state for smart positioning (MUST be before any conditional returns)
  const tooltipRef = useRef(null);
  const [tooltipDimensions, setTooltipDimensions] = useState({ width: 0, height: 0 });
  const [calculatedPosition, setCalculatedPosition] = useState(null);

  // Measure tooltip dimensions after render
  useLayoutEffect(() => {
    if (tooltipRef.current && property) {
      const rect = tooltipRef.current.getBoundingClientRect();
      setTooltipDimensions({
        width: rect.width,
        height: rect.height
      });
    }
  }, [property]); // Re-measure when property changes (different content)

  // Calculate optimal position using smart algorithm
  useLayoutEffect(() => {
    if (!property || !position || !containerRef?.current) {
      setCalculatedPosition(null);
      return;
    }

    // Wait for dimensions to be measured (avoid 0 width/height)
    if (tooltipDimensions.width === 0) return;

    const containerRect = containerRef.current.getBoundingClientRect();

    // Mobile-specific: reduce offset on very small screens
    const offset = containerRect.width < 400 ? 5 : 10;

    const bestPosition = selectBestPosition(
      position,
      tooltipDimensions,
      containerRect,
      offset
    );

    setCalculatedPosition(bestPosition);
  }, [property, position, containerRef, tooltipDimensions]);

  // Early return AFTER all hooks
  if (!property || !position) return null;

  // Get tooltip data
  const identifier = property.id || property.title;
  const rawArea = property.specifications?.area?.total;
  const rawPrice = property.pricing?.totalPrice?.value;

  // Properly check for valid numbers (NaN check)
  const area = Number.isFinite(rawArea) ? rawArea : null;
  const price = Number.isFinite(rawPrice) ? rawPrice : null;
  const typeLabel = property.propertyTypeLabel || property.propertyType;

  // Use calculated position or fallback to default position
  const finalPosition = calculatedPosition || {
    x: position.x + 10,
    y: position.y - 10
  };

  const tooltipStyle = {
    position: 'absolute',
    left: finalPosition.x,
    top: finalPosition.y,
    zIndex: 1000,
    pointerEvents: isMobile ? 'auto' : 'none' // Allow clicking close button on mobile
  };

  return (
    <div ref={tooltipRef} className="map-tooltip" style={tooltipStyle}>
      <div style={{
        backgroundColor: '#1b1b1b',
        color: '#ffffff',
        border: '1px solid #374151',
        borderRadius: '6px',
        padding: '10px 14px',
        fontSize: '0.8125rem',
        fontFamily: "'Sora', sans-serif",
        fontWeight: 400,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        minWidth: '140px',
        maxWidth: '220px',
        position: 'relative'
      }}>
        {/* Close button for mobile */}
        {isMobile && onClose && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              background: 'transparent',
              border: 'none',
              color: '#9CA3AF',
              cursor: 'pointer',
              padding: '4px',
              fontSize: '1rem',
              lineHeight: 1
            }}
          >
            ×
          </button>
        )}

        {/* Identifier */}
        <div style={{
          fontWeight: 600,
          marginBottom: '6px',
          fontSize: '0.875rem',
          color: '#ffffff',
          paddingRight: isMobile ? '20px' : 0
        }}>
          {identifier}
        </div>

        {/* Type label */}
        {typeLabel && (
          <div style={{
            color: '#9CA3AF',
            fontSize: '0.75rem',
            marginBottom: '8px'
          }}>
            {typeLabel}
          </div>
        )}

        {/* Area */}
        {area != null && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '4px',
            color: '#D1D5DB'
          }}>
            <span>Metraż:</span>
            <span style={{ fontWeight: 500, color: '#ffffff' }}>{formatArea(area)}</span>
          </div>
        )}

        {/* Price */}
        {price != null && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: area != null ? '4px' : 0,
            color: '#D1D5DB'
          }}>
            <span>Cena{property.propertyType === 'lokal uslugowy' ? ' netto' : ''}:</span>
            <span style={{ fontWeight: 500, color: '#ffffff' }}>{formatPLN(price)}</span>
          </div>
        )}

        {/* Price per m² */}
        {price != null && area != null && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: '#D1D5DB'
          }}>
            <span>Cena/m²{property.propertyType === 'lokal uslugowy' ? ' netto' : ''}:</span>
            <span style={{ fontWeight: 500, color: '#ffffff' }}>{formatPricePerM2(price, area)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Main SVG overlay component for V2 format
function PropertySVGOverlayV2({
  navigatorConfig,
  properties = [],
  selectedProperty,
  hoveredProperty,
  onPropertyClick,
  onPropertyHover,
  onArtboardChange,
  viewBox,
  enableNavigatorTooltips = false
}) {
  // Track current artboard ID (default to first artboard)
  const [currentArtboardId, setCurrentArtboardId] = useState(null);
  // Track navigation history for back button
  const [navigationHistory, setNavigationHistory] = useState([]);
  // Tooltip state
  const [tooltipData, setTooltipData] = useState({ property: null, position: null });
  // Container ref for tooltip positioning
  const containerRef = useRef(null);
  // Track if device is touch-based (mobile)
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Detect touch device
  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkTouch();
  }, []);

  // Build property lookup map by ID for quick access (with normalized keys for flexible matching)
  const propertiesMap = useMemo(() => {
    const map = {};
    properties.forEach(prop => {
      if (prop.id) {
        // Store by original ID
        map[prop.id] = prop;
        // Also store by normalized ID for flexible matching (e.g., "MP 03" -> "MP3")
        const normalizedKey = normalizeId(prop.id);
        if (normalizedKey !== prop.id) {
          map[normalizedKey] = prop;
        }
      }
    });
    return map;
  }, [properties]);

  // Helper to find property by shape ID (tries normalized matching)
  const findPropertyByShapeId = (shapeId) => {
    // First try exact match
    if (propertiesMap[shapeId]) {
      return propertiesMap[shapeId];
    }
    // Then try normalized match
    const normalizedShapeId = normalizeId(shapeId);
    return propertiesMap[normalizedShapeId] || null;
  };

  // Build artboard lookup map
  const artboardsMap = useMemo(() => {
    const map = {};
    if (navigatorConfig?.artboards) {
      navigatorConfig.artboards.forEach(ab => {
        map[ab.id] = ab;
      });
    }
    return map;
  }, [navigatorConfig]);

  // Get first artboard ID
  const firstArtboardId = navigatorConfig?.artboards?.[0]?.id || null;

  // Get current artboard (use state or default to first)
  const currentArtboard = useMemo(() => {
    const targetId = currentArtboardId || firstArtboardId;
    return targetId ? artboardsMap[targetId] : null;
  }, [currentArtboardId, firstArtboardId, artboardsMap]);

  const shapes = currentArtboard?.shapes || [];

  // Notify parent when artboard changes
  useEffect(() => {
    if (onArtboardChange && currentArtboard) {
      onArtboardChange(currentArtboard);
    }
  }, [currentArtboard, onArtboardChange]);

  // Handle navigation to another artboard
  const handleNavigate = useCallback((targetArtboardId) => {
    if (artboardsMap[targetArtboardId]) {
      // Save current artboard to history for back navigation
      const currentId = currentArtboardId || firstArtboardId;
      if (currentId) {
        setNavigationHistory(prev => [...prev, currentId]);
      }
      setCurrentArtboardId(targetArtboardId);
    }
  }, [artboardsMap, currentArtboardId, firstArtboardId]);

  // Handle back navigation
  const handleGoBack = useCallback(() => {
    if (navigationHistory.length > 0) {
      const previousId = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory(prev => prev.slice(0, -1));
      setCurrentArtboardId(previousId);
    }
  }, [navigationHistory]);

  // Handle direct layer selection from dropdown
  const handleLayerSelect = useCallback((event) => {
    const targetId = event.target.value;
    if (targetId && artboardsMap[targetId]) {
      // Save current artboard to history
      const currentId = currentArtboardId || firstArtboardId;
      if (currentId && currentId !== targetId) {
        setNavigationHistory(prev => [...prev, currentId]);
      }
      setCurrentArtboardId(targetId);
    }
  }, [artboardsMap, currentArtboardId, firstArtboardId]);

  // Get all artboards for layer selector
  const allArtboards = useMemo(() => {
    return navigatorConfig?.artboards || [];
  }, [navigatorConfig]);

  // Close tooltip handler
  const handleCloseTooltip = useCallback(() => {
    setTooltipData({ property: null, position: null });
  }, []);

  // Handle tooltip show/hide based on mouse position
  const handleTooltipUpdate = useCallback((shapeId, event, forceHide = false) => {
    // On mobile, don't auto-hide unless explicitly requested
    if (forceHide || (!shapeId || !event)) {
      if (!isTouchDevice || forceHide) {
        setTooltipData({ property: null, position: null });
      }
      return;
    }

    const property = findPropertyByShapeId(shapeId);

    // Only show tooltip for items that should show tooltip (non-list items)
    // These are items with isTooltipOnly flag OR parking/storage/commercial items
    // Master switch: enableNavigatorTooltips controls ALL tooltips
    // navigatorConfig.tooltipsOnMobile === false wyłącza dymki na dotyku
    // (brak pola = dotychczasowe zachowanie, dymki wszędzie)
    const tooltipsOnMobile = navigatorConfig?.tooltipsOnMobile !== false;
    const shouldShowTooltip = enableNavigatorTooltips && !(isTouchDevice && !tooltipsOnMobile) && property && (
      property.isTooltipOnly === true ||
      property.isStorageUnit === true ||
      property.propertyType === 'miejsce-postojowe' ||
      property.propertyType === 'garaz' ||
      property.propertyType === 'boks-garazowy' ||
      property.propertyType === 'komorka' ||
      property.propertyType === 'komorka-lokatorska' ||
      property.propertyType === 'lokal uslugowy' ||
      property.propertyType === 'lokal-mieszkalny'
    );

    if (shouldShowTooltip && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const position = {
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top
      };
      setTooltipData({ property, position });
    } else if (!isTouchDevice) {
      // On desktop, hide tooltip when hovering non-tooltip items
      setTooltipData({ property: null, position: null });
    }
  }, [findPropertyByShapeId, isTouchDevice, enableNavigatorTooltips, navigatorConfig]);

  // Use provided viewBox or get from current artboard
  const effectiveViewBox = viewBox || currentArtboard?.viewBox || '0 0 100 56.5';

  if (shapes.length === 0) {
    return null;
  }

  const showBackButton = navigationHistory.length > 0;
  const showDropdown = allArtboards.length > 1;
  const currentArtboardIdForSelect = currentArtboardId || firstArtboardId;

  // Only show navigation controls if there's something to show
  const showNavigationControls = showBackButton || showDropdown;

  return (
    <div ref={containerRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
      {/* Navigation controls - only visible when needed */}
      {showNavigationControls && (
        <div className="navigator-controls">
          {/* Back button - only when there's history */}
          {showBackButton && (
            <button
              className="nav-back-btn"
              onClick={handleGoBack}
            >
              ← Wróć
            </button>
          )}

          {/* Layer selector dropdown - only when multiple artboards */}
          {showDropdown && (
            <select
              className="nav-layer-select"
              value={currentArtboardIdForSelect}
              onChange={handleLayerSelect}
            >
              {allArtboards.map(artboard => (
                <option key={artboard.id} value={artboard.id}>
                  {artboard.name || artboard.id}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <svg
        className="property-overlay"
        viewBox={effectiveViewBox}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none'
        }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#f0f0f0" strokeWidth="1" opacity="0.1"/>
          </pattern>
        </defs>

        {shapes.map(shape => {
          const shapeId = getShapeIdentifier(shape);
          const property = findPropertyByShapeId(shapeId);
          return (
            <ShapeRegion
              key={shape.id}
              shape={shape}
              property={property}
              isSelected={selectedProperty === shapeId}
              isHovered={hoveredProperty === shapeId}
              onClick={onPropertyClick}
              onHover={onPropertyHover}
              onNavigate={handleNavigate}
              onTooltip={handleTooltipUpdate}
              isTouchDevice={isTouchDevice}
              currentArtboard={currentArtboard}
              defaultSoldForUnmatched={navigatorConfig?.defaultSoldForUnmatched}
            />
          );
        })}
      </svg>

      {/* Tooltip for non-list items (parking, storage) */}
      <MapTooltip
        property={tooltipData.property}
        position={tooltipData.position}
        containerRef={containerRef}
        isMobile={isTouchDevice}
        onClose={handleCloseTooltip}
      />
    </div>
  );
}

export default PropertySVGOverlayV2;
